/**
 * Quante categorie stanno nella striscia del telefono, e quali restano dietro «Altro».
 *
 * ## Perché non ci stanno tutte
 *
 * Su un'installazione vera le categorie sono decine. Messe tutte in una striscia che scorre di lato,
 * la striscia diventa un secondo rotolo — orizzontale invece che verticale, e peggiore: scorrendo di
 * lato non si sa quanto manca, e le voci passano sotto il pollice senza che se ne veda l'elenco.
 *
 * Quindi se ne mostrano **poche**, e le altre si aprono in un elenco verticale, una per riga, dove
 * si leggono tutte insieme.
 *
 * ## «Tutti» e «Preferiti» non si contano
 *
 * Non sono categorie del catalogo: sono i due modi di **non** filtrare e di guardare solo i propri.
 * Restano sempre nella striscia, qualunque cosa succeda alle altre, perché sono le due voci a cui si
 * torna — e cercare «Tutti» dentro «Altro» sarebbe cercare la via d'uscita dentro il labirinto.
 *
 * ## La scelta corrente resta visibile
 *
 * Se la categoria attiva è fra quelle nascoste, prende il posto dell'ultima visibile. Senza, si
 * sceglie «Documentari» da «Altro» e la striscia torna a non mostrarlo: lo schermo direbbe che il
 * filtro è su «Tutti» mentre l'elenco sotto è filtrato — la forma più fastidiosa di bugia, perché
 * sembra un difetto dell'elenco.
 */

import { CATEGORY_ALL, CATEGORY_FAVORITES, type GuideCategory } from './guideCategories.js';

/**
 * Quante categorie del catalogo entrano nella striscia, oltre a «Tutti» e «Preferiti».
 *
 * Quattro: con le due fisse fanno sei pastiglie, che su uno schermo da 360 punti si vedono senza
 * scorrere fino in fondo. Di più e tornerebbe il rotolo; di meno e «Altro» si aprirebbe anche su
 * cataloghi piccoli, dove non serve.
 */
export const PHONE_CATEGORY_SLOTS = 4;

export interface SplitCategories {
  /** Quelle da mostrare come pastiglie, nell'ordine in cui vanno disegnate. */
  readonly shown: ReadonlyArray<GuideCategory>;
  /** Quelle dietro «Altro». Vuoto quando ci stanno tutte: allora il tasto non si disegna. */
  readonly hidden: ReadonlyArray<GuideCategory>;
}

export function splitPhoneCategories(
  categories: ReadonlyArray<GuideCategory>,
  activeCategoryId: string,
  slots: number = PHONE_CATEGORY_SLOTS,
): SplitCategories {
  const fisse = categories.filter((c) => c.id === CATEGORY_ALL || c.id === CATEGORY_FAVORITES);
  const delCatalogo = categories.filter((c) => c.id !== CATEGORY_ALL && c.id !== CATEGORY_FAVORITES);

  if (delCatalogo.length <= slots) {
    return { shown: [...fisse, ...delCatalogo], hidden: [] };
  }

  const visibili = delCatalogo.slice(0, slots);
  const nascoste = delCatalogo.slice(slots);

  /*
    La scelta corrente, se è fra le nascoste, entra **al posto dell'ultima visibile**.

    Aggiungerla in coda invece di sostituire farebbe crescere la striscia di una pastiglia ogni volta
    che si sceglie da «Altro», cioè rimetterebbe il rotolo un passo per volta.
  */
  const attivaNascosta = nascoste.find((c) => c.id === activeCategoryId);
  if (attivaNascosta === undefined) {
    return { shown: [...fisse, ...visibili], hidden: nascoste };
  }

  return {
    shown: [...fisse, ...visibili.slice(0, -1), attivaNascosta],
    hidden: [visibili[visibili.length - 1] as GuideCategory, ...nascoste.filter((c) => c.id !== activeCategoryId)],
  };
}
